import type { Event } from '@bugsnag/js'
import { doitEtreNotifiee } from '~/utils/bugsnag-filter'

/**
 * Écarte de Bugsnag les erreurs du fonctionnement normal — 401, 403, 404.
 *
 * Le module `nuxt-bugsnag` ne propose pas de filtre, et son `nuxt.config.ts` ne
 * peut pas en porter un : le module étale `options.config` dans
 * `runtimeConfig.public.bugsnag`, que ses plugins relisent au démarrage, or le
 * `runtimeConfig` est sérialisé et une fonction n'y survit pas. Poser `onError`
 * dans la configuration du module serait donc sans effet.
 *
 * Le filtre passe par `addOnError()`, l'API du SDK prévue pour enregistrer un
 * callback après le démarrage. Un callback qui renvoie `false` empêche l'envoi
 * de l'événement — il n'est donc pas décompté du quota, là où une règle posée
 * dans l'interface de Bugsnag le consommerait quand même.
 *
 * Ce plugin s'exécute après celui du module : `nuxt-bugsnag` enregistre le sien
 * par `addPlugin()` lors du `setup()`, donc avant les plugins applicatifs de
 * `app/plugins/`. Il n'est volontairement pas suffixé `.client` : le plugin du
 * module ne l'est pas non plus et tourne aussi au rendu serveur.
 *
 * Le détail des codes retenus et leur justification sont dans
 * `app/utils/bugsnag-filter.ts`, où le filtre vit depuis le plugin maison
 * qu'il remplace — il y est déjà couvert par `tests/bugsnag-filter.test.ts`.
 * Le pendant Nitro est dans `server/plugins/bugsnag-filtre.ts`.
 */
export default defineNuxtPlugin(() => {
  /*
   * `useBugsnag()` est l'auto-import fourni par le module — mais il n'existe
   * pas toujours.
   *
   * Quand l'auto-import n'a pas été déclaré, la référence ne tombe pas sur un
   * client factice : elle n'est liée à rien, et l'évaluation lève
   * `useBugsnag is not defined`. Le cas a été constaté en conditions réelles
   * sur `app-admin-site`, toutes les pages en 500.
   *
   * La cause exacte de la disparition de l'auto-import n'est pas tranchée ici —
   * le hook `nitro:config` du module ne la conditionne pas à la présence de la
   * clé. Cette garde ne dépend pas de cette réponse : elle protège de
   * l'auto-import absent quelle qu'en soit la raison, et ne coûte rien quand il
   * est présent.
   *
   * Les deux tests qui suivent sont complémentaires : `typeof useBugsnag`
   * distingue « fonction absente », `bugsnag?.addOnError` distingue « client
   * factice » — celui que le module pose quand Bugsnag n'a pas démarré (clé
   * absente, `development` hors des `enabledReleaseStages`), et dont
   * `addOnError` est une fonction vide.
   */
  const bugsnag = typeof useBugsnag === 'function' ? useBugsnag() : null

  if (typeof bugsnag?.addOnError !== 'function') return

  bugsnag.addOnError((event: Event) => {
    // `false` annule l'envoi ; toute autre valeur le laisse passer.
    return doitEtreNotifiee(event.originalError)
  })
})
